import {FC, useEffect, useRef} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import Snackbar from 'components/Basic/Snackbar'
import {KEY_CODES, SPARKLE_EMOJI} from 'definitions/constant'

import {FeatureHighlightMeta} from './types'

type FeatureHighlightSnackbarProps = {
  feature: FeatureHighlightMeta
  show: boolean
  onClose: () => void
  onOkay: () => void
}

/** Other overlays that should own Escape / outside-click before this highlight dismisses. */
const OTHER_OPEN_OVERLAY_SELECTOR = [
  '[aria-modal="true"]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  // Shared Modal has no dialog role and does not steal focus from the highlight CTA.
  '[data-cy="notification-modal"]',
].join(', ')

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const getFocusableElements = (root: Element): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    element => element.tabIndex !== -1,
  )

const trapTabFocus = (event: KeyboardEvent, highlightRoot: Element): void => {
  const focusable = getFocusableElements(highlightRoot)
  const first = focusable[0]
  const last = focusable.at(-1)
  if (!first || !last) return

  const active = document.activeElement
  const focusOutside =
    !(active instanceof Node) || !highlightRoot.contains(active)

  if (event.shiftKey) {
    if (focusOutside || active === first) {
      event.preventDefault()
      last.focus({preventScroll: true})
    }
    return
  }

  if (focusOutside || active === last) {
    event.preventDefault()
    first.focus({preventScroll: true})
  }
}

const hasOtherOpenOverlay = (highlightDataCy: string): boolean =>
  Array.from(document.querySelectorAll(OTHER_OPEN_OVERLAY_SELECTOR)).some(
    node => node instanceof HTMLElement && node.dataset.cy !== highlightDataCy,
  )

const shouldDismissHighlightOnEscape = (
  event: KeyboardEvent,
  highlightRoot: Element,
  highlightDataCy: string,
): boolean => {
  // Another overlay already handled Esc, or a different dialog owns the layer.
  if (event.defaultPrevented) return false
  // Shared Modal does not steal focus; require highlight focus AND no other overlay.
  if (
    !(
      document.activeElement instanceof Node &&
      highlightRoot.contains(document.activeElement)
    )
  ) {
    return false
  }
  return !hasOtherOpenOverlay(highlightDataCy)
}

const shouldDismissHighlightOnOutsidePointer = (
  event: PointerEvent,
  highlightRoot: Element,
  highlightDataCy: string,
): boolean => {
  if (event.defaultPrevented) return false
  if (!(event.target instanceof Node)) return false
  // Keep the underlying click; only dismiss when the pointer is outside.
  if (highlightRoot.contains(event.target)) return false
  return !hasOtherOpenOverlay(highlightDataCy)
}

const FeatureHighlightSnackbar: FC<FeatureHighlightSnackbarProps> = ({
  feature,
  show,
  onClose,
  onOkay,
}) => {
  const namespace = feature.i18nNamespace ?? 'aiUsage'
  // Host mounts outside the route <Suspense>; suspense would bubble to ErrorBoundary.
  // With useSuspense: false, t() returns keys until the namespace loads — wait for ready.
  const {t, ready} = useTranslation([namespace], {useSuspense: false})
  const dataCy = `${feature.id}-feature-highlight-snackbar`
  const titleId = `${feature.id}-feature-highlight-title`
  const ctaRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const eyebrow = feature.eyebrowKey
    ? t(feature.eyebrowKey, {ns: namespace, emoji: SPARKLE_EMOJI})
    : `${SPARKLE_EMOJI} New feature`
  const skipLabel = feature.skipLabelKey
    ? t(feature.skipLabelKey, {ns: namespace})
    : t('FEATURE_HIGHLIGHT_SKIP', {ns: namespace})

  const isVisible = show && ready

  useEffect(() => {
    if (!isVisible) return undefined

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    const focusCta = () => {
      ctaRef.current?.focus({preventScroll: true})
    }
    focusCta()
    // Chrome can miss the first focus pass while the snackbar finishes mounting.
    const rafId = window.requestAnimationFrame(focusCta)

    const onKeyDown = (event: KeyboardEvent) => {
      const highlightRoot = document.querySelector(`[data-cy="${dataCy}"]`)
      if (!highlightRoot) return

      if (event.key === KEY_CODES.TAB) {
        trapTabFocus(event, highlightRoot)
        return
      }

      if (event.key !== KEY_CODES.ESCAPE) return
      if (!shouldDismissHighlightOnEscape(event, highlightRoot, dataCy)) return
      onClose()
    }

    // Bubble-phase only: dismiss without blocking the underlying page click.
    const onPointerDown = (event: PointerEvent) => {
      const highlightRoot = document.querySelector(`[data-cy="${dataCy}"]`)
      if (!highlightRoot) return
      if (
        !shouldDismissHighlightOnOutsidePointer(event, highlightRoot, dataCy)
      ) {
        return
      }
      // Keep focus on the control the user clicked; do not restore the prior CTA.
      previousFocusRef.current = null
      onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.cancelAnimationFrame(rafId)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
      const previous = previousFocusRef.current
      previousFocusRef.current = null
      if (previous?.isConnected) {
        previous.focus({preventScroll: true})
      }
    }
  }, [dataCy, isVisible, onClose])

  return (
    <Snackbar
      show={isVisible}
      position="middle-middle"
      variant="feature_highlight"
      role="dialog"
      ariaModal
      ariaLabelledBy={titleId}
      isCopyLoading={false}
      showCopy={false}
      dismissible
      onClose={onClose}
      dataCy={dataCy}
      className="z-toast max-w-md items-stretch shadow-lg"
    >
      <div className="w-full">
        <p className="text-base karla tracking-wide text-white/75 mb-2">
          {eyebrow}
        </p>
        <h2
          id={titleId}
          className="text-base abc text-white tracking-[-0.02em]"
        >
          {t(feature.titleKey, {ns: namespace})}
        </h2>
        <p className="mt-2 mr-2 text-sm karla text-white/80 leading-relaxed">
          {t(feature.messageKey, {ns: namespace})}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            data-cy={`${dataCy}-skip`}
            onClick={onClose}
            className={classNames(
              'px-3 py-1.5 text-sm karla rounded-lg',
              'border border-white/20 bg-white/[0.04] text-white/80',
              'hover:bg-white/10 hover:text-white transition-colors',
            )}
          >
            {skipLabel}
          </button>
          <button
            ref={ctaRef}
            type="button"
            data-cy={`${dataCy}-cta`}
            onClick={onOkay}
            className={classNames(
              'px-3 py-1.5 text-sm karla-bold rounded-lg',
              'border border-white/30 bg-white/90 text-dope-black',
              'hover:bg-white transition-colors',
            )}
          >
            {t(feature.ctaLabelKey, {ns: namespace})}
          </button>
        </div>
      </div>
    </Snackbar>
  )
}

export default FeatureHighlightSnackbar
